// Manufacturing → Orders mode panel.
// Primary action: Save links (per-deal recipe attachments).
import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ManufacturingGateBanner } from "./ManufacturingGateBanner";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { useManufacturingOrdersTab } from "./useManufacturingOrdersTab";
import { ManufacturingOrdersCogsCommitLedger } from "./ManufacturingOrdersCogsCommitLedger";
import { ghostBtnClass, inputClass, mfgEmptyStateClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

function fmtUsd(n: number) {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

export function ManufacturingOrdersPanel() {
  const gate = useManufacturingEntitlementGate();
  const [navTick, setNavTick] = useState(0);
  const o = useManufacturingOrdersTab(navTick);

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Orders</h2>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => {
            gate.refresh();
            setNavTick((s) => s + 1);
          }}
          disabled={gate.loading || o.dealsBusy || o.recipesBusy || o.detailBusy}
        >
          {o.dealsBusy || o.recipesBusy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <ManufacturingGateBanner gate={gate} />
      {gate.enabled && o.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {o.localError}
        </div>
      ) : null}

      {gate.enabled ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <SectionCard
            title="Deals"
            description="Link each order (deal) to one or more build models. Materials cost uses the same nested BOM rollup as the Models tab."
          >
            {o.dealsBusy ? (
              <div className="text-sm text-gray-500 dark:text-zinc-400">Loading deals…</div>
            ) : null}
            {!o.dealsBusy && o.deals.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-zinc-400">No deals in this org yet.</div>
            ) : null}
            {!o.dealsBusy && o.deals.length > 0 ? (
              <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
                {o.deals.map((d) => {
                  const isActive = o.selectedDealId === d.id;
                  return (
                    <li key={d.id}>
                      <button
                        type="button"
                        className={
                          "w-full rounded-md px-3 py-2 text-left text-sm transition-colors " +
                          (isActive
                            ? "bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-100"
                            : "text-gray-800 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800/60")
                        }
                        onClick={() => o.setSelectedDealId(d.id)}
                      >
                        <div className="font-medium">{d.display_name?.trim() || "Untitled deal"}</div>
                        <div className="text-xs text-gray-600 dark:text-zinc-400">{d.status}</div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </SectionCard>

          <div className="space-y-4">
            {!o.selectedDealId ? (
              <div className={mfgEmptyStateClass}>
                Select a deal to attach models and preview materials cost.
              </div>
            ) : (
              <>
                <SectionCard
                  title="Recipe links"
                  description={
                    <>
                      Sale price (optional) overrides deal metadata for margin: use keys{" "}
                      <code>mfg_sale_price_usd</code> or <code>sale_price_usd</code> on the deal, or enter
                      below.
                    </>
                  }
                >
                  <label className="block text-xs text-gray-600 dark:text-zinc-300">
                    Sale price (USD)
                    <input
                      className={`${inputClass} mt-1`}
                      value={o.salePriceInput}
                      onChange={(e) => o.setSalePriceInput(e.target.value)}
                      inputMode="decimal"
                      placeholder="From deal metadata or override"
                    />
                  </label>
                  {o.runPreviewNow ? (
                    <button type="button" className={ghostBtnClass} onClick={() => o.runPreviewNow?.()}>
                      Refresh preview now
                    </button>
                  ) : null}
                </SectionCard>

                <SectionCard title="Linked models">
                  {o.detailBusy || o.recipesBusy ? (
                    <div className="text-xs text-gray-500 dark:text-zinc-400">Loading…</div>
                  ) : null}
                  {!o.recipes.length ? (
                    <div className="text-sm text-gray-500 dark:text-zinc-400">
                      Create a model first under the Models tab.
                    </div>
                  ) : null}
                  <div className="space-y-2">
                    {o.linkRows.map((row) => (
                      <div key={row.rowId} className="flex flex-wrap items-center gap-2">
                        <select
                          className={`${inputClass} flex-1 min-w-[140px]`}
                          value={row.recipe_id}
                          onChange={(e) => o.updateLinkRow(row.rowId, { recipe_id: e.target.value })}
                        >
                          {o.recipes.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.code} · {r.name}
                            </option>
                          ))}
                        </select>
                        <input
                          className={`${inputClass} max-w-[80px]`}
                          value={row.quantity}
                          onChange={(e) => o.updateLinkRow(row.rowId, { quantity: e.target.value })}
                          inputMode="decimal"
                        />
                        <button
                          type="button"
                          className={ghostBtnClass}
                          onClick={() => o.removeLinkRow(row.rowId)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={ghostBtnClass}
                      onClick={o.addLinkRow}
                      disabled={!o.defaultRecipeId}
                    >
                      + Model line
                    </button>
                    <button
                      type="button"
                      className={mfgPrimaryBtnClass}
                      onClick={() => void o.saveLinks()}
                      disabled={o.saveBusy || !o.recipes.length}
                    >
                      {o.saveBusy ? "Saving…" : "Save links"}
                    </button>
                  </div>
                </SectionCard>

                <SectionCard
                  title="Materials preview"
                  description="Includes catalog attachments on each model (variants + default-in packages) with transform rules applied."
                >
                  {!o.preview ? (
                    <div className="text-sm text-gray-500 dark:text-zinc-400">
                      {o.detailBusy ? "…" : "No preview yet."}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {o.preview.resolution_warnings && o.preview.resolution_warnings.length > 0 ? (
                        <ul className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                          {o.preview.resolution_warnings.map((w) => (
                            <li key={w}>{w}</li>
                          ))}
                        </ul>
                      ) : null}
                      <div className="text-xs text-gray-600 dark:text-zinc-400">
                        Roll-up:{" "}
                        {o.preview.rollup_mode === "nested_materials"
                          ? "Nested (sub-recipes)"
                          : "Flat materials only"}
                      </div>
                      <div className="text-base font-semibold text-gray-900 dark:text-zinc-100">
                        Estimated materials: {fmtUsd(o.preview.estimated_materials_cost)}
                      </div>
                      {(o.preview.named_variant_wholesale_price_delta_sum != null &&
                        o.preview.named_variant_wholesale_price_delta_sum !== 0) ||
                      (o.preview.named_variant_retail_price_delta_sum != null &&
                        o.preview.named_variant_retail_price_delta_sum !== 0) ? (
                        <div className="text-xs text-gray-600 dark:text-zinc-400">
                          Named variant upcharges — wholesale{" "}
                          {fmtUsd(o.preview.named_variant_wholesale_price_delta_sum ?? 0)}
                          {" · "}
                          retail {fmtUsd(o.preview.named_variant_retail_price_delta_sum ?? 0)}
                          {o.preview.applied_named_variant_application_ids?.length
                            ? ` (${o.preview.applied_named_variant_application_ids.length} applied)`
                            : ""}
                        </div>
                      ) : null}
                      {o.preview.sale_price_usd != null ? (
                        <div className="text-xs text-gray-600 dark:text-zinc-400">
                          Sale: {fmtUsd(o.preview.sale_price_usd)}
                          {o.preview.materials_margin_usd != null ? (
                            <> · Materials margin: {fmtUsd(o.preview.materials_margin_usd)}</>
                          ) : null}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-600 dark:text-zinc-400">
                          No sale price — set metadata or enter above for margin.
                        </div>
                      )}
                      <div className="text-xs font-medium text-gray-700 dark:text-zinc-300">Per model</div>
                      <ul className="ml-4 list-disc text-xs text-gray-600 dark:text-zinc-400">
                        {o.preview.per_recipe.map((p) => (
                          <li key={p.recipe_id}>
                            {p.recipe_code} × {p.recipe_quantity}: unit {fmtUsd(p.unit_materials_cost)} →
                            extended {fmtUsd(p.extended_materials_cost)} (
                            {p.rollup_mode === "nested_materials" ? "nested" : "flat"})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </SectionCard>

                <SectionCard
                  title="Full COGS preview"
                  description="Live predictive FullCost = materials + labor + applied overhead + PTL (§3.1). Matches the spreadsheet model for pilot SKU spot-checks."
                >
                  {!o.fullCogsPreview ? (
                    <div className="text-sm text-gray-500 dark:text-zinc-400">
                      {o.detailBusy ? "…" : "Save recipe links to preview full COGS."}
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      {o.fullCogsPreview.resolution_warnings?.length ? (
                        <ul className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                          {o.fullCogsPreview.resolution_warnings.map((w) => (
                            <li key={w}>{w}</li>
                          ))}
                        </ul>
                      ) : null}
                      <div className="grid gap-1 text-xs text-gray-600 dark:text-zinc-400">
                        <div>Materials: {fmtUsd(o.fullCogsPreview.materials_cost_usd ?? 0)}</div>
                        <div>Labor: {fmtUsd(o.fullCogsPreview.labor_cost_usd ?? 0)}</div>
                        <div>
                          Applied overhead
                          {o.fullCogsPreview.overhead_rate != null
                            ? ` (${(o.fullCogsPreview.overhead_rate * 100).toFixed(1)}%)`
                            : ""}
                          : {fmtUsd(o.fullCogsPreview.applied_overhead_usd ?? 0)}
                        </div>
                        <div>PTL: {fmtUsd(o.fullCogsPreview.ptl_amount_usd ?? 0)}</div>
                      </div>
                      <div className="text-base font-semibold text-gray-900 dark:text-zinc-100">
                        FullCost: {fmtUsd(o.fullCogsPreview.full_cost_usd ?? 0)}
                      </div>
                      {o.fullCogsPreview.channel_prices_usd.retail != null ? (
                        <div className="text-xs text-gray-600 dark:text-zinc-400">
                          Catalog retail: {fmtUsd(o.fullCogsPreview.channel_prices_usd.retail)}
                          {o.fullCogsPreview.channel_prices_usd.wholesale != null
                            ? ` · wholesale ${fmtUsd(o.fullCogsPreview.channel_prices_usd.wholesale)}`
                            : ""}
                        </div>
                      ) : null}
                      {o.fullCogsPreview.sale_price_usd != null && o.fullCogsPreview.full_cost_usd != null ? (
                        <div className="text-xs text-gray-600 dark:text-zinc-400">
                          Sale: {fmtUsd(o.fullCogsPreview.sale_price_usd)}
                          {o.fullCogsPreview.full_cost_margin_usd != null ? (
                            <>
                              {" "}
                              · Margin: {fmtUsd(o.fullCogsPreview.full_cost_margin_usd)}
                              {o.fullCogsPreview.full_cost_margin_pct != null
                                ? ` (${o.fullCogsPreview.full_cost_margin_pct}%)`
                                : ""}
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  )}
                </SectionCard>

                {o.preview?.resolved_materials && o.preview.resolved_materials.length > 0 ? (
                  <SectionCard
                    title="Materials preview lines"
                    description="Resolved BOM leaves from materials-preview."
                  >
                    <ul className="divide-y divide-gray-100 text-xs dark:divide-zinc-800">
                      {o.preview.resolved_materials.map((ln) => (
                        <li key={`${ln.material_id}-${ln.material_code}`} className="flex justify-between gap-2 py-1">
                          <span className="min-w-0 truncate">
                            <span className="font-mono font-semibold">{ln.material_code}</span>{" "}
                            {ln.material_name}
                          </span>
                          <span className="font-mono text-gray-600 dark:text-zinc-400">
                            {ln.quantity} × {fmtUsd(ln.unit_cost)} = {fmtUsd(ln.extended_cost)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </SectionCard>
                ) : null}

                <SectionCard
                  title="COGS snapshot commits"
                  description="Immutable freeze in order: materials → build → sale. Legacy materials-only snapshots remain readable."
                >
                  <ManufacturingOrdersCogsCommitLedger
                    fullCogsPreview={o.fullCogsPreview}
                    materialsSnapshot={o.materialsSnapshot}
                    buildSnapshot={o.buildSnapshot}
                    saleSnapshot={o.snapshot}
                    commitBusy={o.commitBusy}
                    onCommit={(kind) => o.commitSnapshot?.(kind)}
                  />
                  {o.materialsSnapshot && o.issuanceActualUsd != null ? (
                    <div className="mt-3 rounded-lg border border-gray-200 px-3 py-2 text-xs dark:border-zinc-700">
                      <div className="font-medium text-gray-800 dark:text-zinc-200">
                        Materials reconcile
                      </div>
                      <div className="mt-1 flex flex-wrap gap-3 text-gray-600 dark:text-zinc-400">
                        <span>
                          Committed est.{" "}
                          {fmtUsd(
                            o.materialsSnapshot.materials_cost_usd ??
                              o.materialsSnapshot.estimated_materials_cost,
                          )}
                        </span>
                        <span>Actual issued {fmtUsd(o.issuanceActualUsd)}</span>
                        <span>
                          Variance{" "}
                          {fmtUsd(
                            o.issuanceActualUsd -
                              (o.materialsSnapshot.materials_cost_usd ??
                                o.materialsSnapshot.estimated_materials_cost),
                          )}
                        </span>
                      </div>
                    </div>
                  ) : null}
                  {o.materialsSnapshot?.is_legacy_materials_only ? (
                    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-300">
                      Legacy materials_commit snapshot preserved (
                      {fmtUsd(o.materialsSnapshot.estimated_materials_cost)} ·{" "}
                      {new Date(o.materialsSnapshot.created_at).toLocaleString()}).
                    </div>
                  ) : null}
                </SectionCard>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
